// userFacingErrorMessage — turns a raw JS/SDK error into a short, safe
// string for end users. Two call sites hit this: the AI chat transport
// (simoneAiTransport.js onError) and the org/workspace switcher's failure
// toast (AppOrgSwitcher.js _commitOrgSwitch) — both used to show the raw
// `err.message`, which for a network-class failure is the SDK's
// diagnostic string built for developers (host, VPN/DNS/adblocker hints,
// the literal "Failed to fetch") — alarming and meaningless to a user.
//
// Network-class detection mirrors the SDK's own classifier
// (sdk/src/services/BaseService.js `_isNetworkFailure` / `NETWORK_UNREACHABLE`):
// prefer the tagged `err.code`, fall back to matching the message shape in
// case an untagged fetch-layer TypeError slips through.
//
// The raw error should still go to console.warn/console.error at the call
// site for debugging — only the string shown to the user goes through here.
//
// FA204/FA207 / frank trap — `_NETWORK_ERROR_RX` (module-scope) and
// `isNetworkError` (a sibling export, read bare by userFacingErrorMessage)
// are both unresolvable once this file's exports are individually
// frank.toJSON'd for platform delivery
// (project_frank_drops_module_local_helpers; readerVoice.js precedent).
// Both are now inlined self-contained in each consumer.
export function isNetworkError(err) {
  const NETWORK_ERROR_RX = /failed to fetch|network unreachable|networkerror|load failed/i
  return err?.code === 'NETWORK_UNREACHABLE' || NETWORK_ERROR_RX.test(err?.message || '')
}

// AIUX-CHROME item 4 (tickets/sonnet.md, live-verified 2026-08-13) — the AI
// transport's provider/stream-integrity failures carry developer diagnostics
// in `err.message` (SSE-frame accounting, a raw backend URL) that used to
// leak straight into the chat bubble via the generic `fallback`'s
// `(${msg})` interpolation: "Symbols Service produced no output — empty SSE
// stream from https://symbols-service-production.up.railway.app/api/chat (no
// deltas, no tool calls, no error frame)". Detected the same way
// `isNetworkError` detects its class — a message-shape regex — so this needs
// no server-side error-code plumbing to cover both the thrown-error path
// (onError below) and any other caller that lands a raw provider message
// here.
export function isTransportIntegrityError(err) {
  const STREAM_INTEGRITY_RX =
    /empty sse stream|no deltas,?\s*no tool calls|no error frame|ai provider unavailable|produced no output|reported usage|incomplete\/corrupted|corrupted completion/i
  return STREAM_INTEGRITY_RX.test(err?.message || '')
}

// `fallback` lets each call site keep its own phrasing/prefix for the
// non-network, non-stream-integrity case (e.g. "Sorry — that request failed
// (...)" vs "Couldn't switch to <org>") while sharing the network-vs-not
// branch and the never-show-a-URL guarantee.
export function userFacingErrorMessage(err, { networkMessage, streamMessage, fallback } = {}) {
  const NETWORK_ERROR_RX = /failed to fetch|network unreachable|networkerror|load failed/i
  const isNetwork = err?.code === 'NETWORK_UNREACHABLE' || NETWORK_ERROR_RX.test(err?.message || '')
  if (isNetwork) {
    return networkMessage || "Couldn't reach the server — check your connection and try again."
  }
  if (isTransportIntegrityError(err)) {
    return (
      streamMessage ||
      "The AI service didn't respond cleanly. Try again — if it keeps happening, switching models often helps."
    )
  }
  if (typeof fallback === 'function') return fallback(err)
  const msg = (err?.message || String(err) || '').trim()
  return msg || 'Something went wrong.'
}
